import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type {
  DocStatus, DocDocument, DocBrand, DocTemplate, BrandConfig, DocTrack,
} from '../../../types/docops';
import {
  getAllBrands, saveBrand,
  getDocumentsByBrand, saveDocument,
  getAllTemplates, getAllCategories,
} from '../../../services/firestore/docopsService';
import { DEFAULT_BRAND_CONFIG } from '../../../data/docops/defaultBrandConfig';
import { computeLibraryScore } from '../../../services/ai/libraryScoring';
import { useDocumentGenerator } from '../../../hooks/useDocumentGenerator';
import { useDocExport } from '../../../hooks/useDocExport';
import { usePipelineData } from '../../../hooks/usePipelineData';
import { C, PIPELINE_STAGES } from './docopsConstants';
import { PipelineView } from './PipelineView';
import { DocumentView } from './DocumentView';
import { ConfigView } from './ConfigView';
import type { LibraryScore } from '../../../types/docops';
import './DocumentOpsCenter.css';

// -- MAIN COMPONENT --
export function DocumentOpsCenter() {
  const [view, setView] = useState<'pipeline' | 'document' | 'config'>('pipeline');
  const [loading, setLoading] = useState(true);

  // Data
  const [brands, setBrands] = useState<DocBrand[]>([]);
  const [activeBrand, setActiveBrand] = useState<DocBrand | null>(null);
  const [documents, setDocuments] = useState<DocDocument[]>([]);
  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  // Navigation & Filtering
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('hemologix_collapsed_stages');
      if (stored) return new Set(JSON.parse(stored) as string[]);
    } catch { /* ignore */ }
    // Default: all collapsed
    return new Set(PIPELINE_STAGES.map(s => s.id));
  });
  const preGenCollapsed = useRef<Set<string> | null>(null);

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem('hemologix_collapsed_stages', JSON.stringify([...collapsedStages]));
  }, [collapsedStages]);
  const [showConflicts, setShowConflicts] = useState(true);

  // Track filter (legal = main pipeline, others = separate tracks)
  const [activeTrack, setActiveTrack] = useState<DocTrack>('legal');

  // Filter documents/templates by active track
  const legalTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const t of templates) {
      if ((t.track ?? 'legal') === activeTrack) ids.add(t.id);
    }
    return ids;
  }, [templates, activeTrack]);

  const filteredDocuments = useMemo(
    () => documents.filter(d => legalTemplateIds.has(d.templateId)),
    [documents, legalTemplateIds],
  );

  const filteredTemplates = useMemo(
    () => templates.filter(t => (t.track ?? 'legal') === activeTrack),
    [templates, activeTrack],
  );

  // Track counts for selector UI
  const trackCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of templates) {
      const track = t.track ?? 'legal';
      counts[track] = (counts[track] ?? 0) + 1;
    }
    return counts;
  }, [templates]);

  // Status saving (shared between Pipeline + Document views)
  const [saving, setSaving] = useState(false);

  // Library score
  const [libraryScore, setLibraryScore] = useState<LibraryScore | null>(null);

  const handleDocUpdated = (doc: DocDocument) => {
    setDocuments(prev => prev.map(d => d.id === doc.id ? doc : d));
  };

  // Refresh documents from Firestore (called by hook after server-side job completes)
  const refreshDocs = useCallback(async () => {
    if (!activeBrand) return;
    const docs = await getDocumentsByBrand(activeBrand.id);
    setDocuments(docs);
  }, [activeBrand]);

  // -- HOOKS --

  const {
    generating, generatingAll, fixingConflicts, fixProgress, genProgress, auditProgress,
    genError, genResult, genMsgIdx, activeJobId, jobDocIds, preRegenScores,
    generateSingle, generateBatch, generateNextRound, generateByPhase, applyConfigToAll, rescoreUngated, rescoreAll,
    fixConflict, fixAllConflicts, cancelJob,
    clearGenError, clearGenResult,
  } = useDocumentGenerator({
    brandId: activeBrand?.id ?? null,
    config: activeBrand?.config ?? null,
    templates,
    documents,
    onDocumentUpdated: handleDocUpdated,
    refreshDocuments: refreshDocs,
  });

  const { totalDocs, blockerDocs, attorneyDocs, generatedDocs, pipelineGroups, nextRoundInfo } =
    usePipelineData({ documents: filteredDocuments, templates: filteredTemplates, libraryScore, jobDocIds });

  const { exporting, downloadAllDocs } =
    useDocExport({ documents: filteredDocuments, activeBrand, libraryScore });

  // Restore collapsed stages when generation finishes
  useEffect(() => {
    if (!generatingAll && preGenCollapsed.current) {
      setCollapsedStages(preGenCollapsed.current);
      preGenCollapsed.current = null;
    }
  }, [generatingAll]);

  // Collapse all stages (called when user starts generation, not on reconnect)
  const collapseForGeneration = useCallback(() => {
    preGenCollapsed.current = new Set(collapsedStages);
    setCollapsedStages(new Set(Object.keys(pipelineGroups)));
  }, [collapsedStages, pipelineGroups]);

  // -- DATA LOADING --
  useEffect(() => {
    (async () => {
      try {
        const [rawBrands, t, c] = await Promise.all([
          getAllBrands(), getAllTemplates(), getAllCategories(),
        ]);
        const b = rawBrands.map(brand => ({
          ...brand,
          config: { ...DEFAULT_BRAND_CONFIG.config, ...brand.config },
        }));
        setBrands(b);
        setTemplates(t);
        // categories loaded for future use
        void c;
        if (b.length > 0) setActiveBrand(b[0]);
      } catch (err) {
        console.error('Failed to load doc ops data:', err);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!activeBrand) return;
    (async () => {
      const docs = await getDocumentsByBrand(activeBrand.id);
      const stuck = docs.filter(d => d.status === 'generating');
      for (const d of stuck) {
        const fixed = { ...d, status: 'not_started' as const, lastEditedAt: new Date().toISOString() };
        try { await saveDocument(activeBrand.id, fixed); } catch { /* best effort */ }
        docs[docs.indexOf(d)] = fixed;
      }
      if (stuck.length > 0) {
        console.log(`Reset ${stuck.length} stuck "generating" doc(s) to "not_started"`);
      }
      setDocuments(docs);
    })();
  }, [activeBrand]);

  // Recompute library score using track-filtered documents/templates
  useEffect(() => {
    if (filteredDocuments.length > 0 && filteredTemplates.length > 0 && activeBrand) {
      const score = computeLibraryScore(filteredDocuments, filteredTemplates, activeBrand.config);
      setLibraryScore(score);
    } else {
      setLibraryScore(null);
    }
  }, [filteredDocuments, filteredTemplates, activeBrand]);

  // -- NAVIGATION --

  const openDocument = (docId: string) => {
    setSelectedDocId(docId);
    clearGenResult();
    clearGenError();
    setView('document');
  };

  const goBack = () => setView('pipeline');
  const openConfig = () => setView('config');

  const toggleFilter = (filter: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(filter)) next.delete(filter);
      else next.add(filter);
      return next;
    });
  };

  const toggleStage = (stageId: string) => {
    setCollapsedStages(prev => {
      const next = new Set(prev);
      if (next.has(stageId)) next.delete(stageId);
      else next.add(stageId);
      return next;
    });
  };

  // -- STATUS UPDATES --

  const updateDocStatus = async (docId: string, newStatus: DocStatus) => {
    if (!activeBrand) return;
    const docToUpdate = documents.find(d => d.id === docId);
    if (!docToUpdate) return;
    const updated: DocDocument = {
      ...docToUpdate,
      status: newStatus,
      lastEditedAt: new Date().toISOString(),
    };
    setSaving(true);
    try {
      await saveDocument(activeBrand.id, updated);
      setDocuments(prev => prev.map(d => d.id === docId ? updated : d));
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setSaving(false);
    }
  };

  const bulkUpdateStatus = async (docIds: string[], newStatus: DocStatus) => {
    if (!activeBrand) return;
    setSaving(true);
    try {
      for (const docId of docIds) {
        const docToUpdate = documents.find(d => d.id === docId);
        if (!docToUpdate) continue;
        const updated: DocDocument = {
          ...docToUpdate,
          status: newStatus,
          lastEditedAt: new Date().toISOString(),
        };
        await saveDocument(activeBrand.id, updated);
        setDocuments(prev => prev.map(d => d.id === docId ? updated : d));
      }
    } catch (err) {
      console.error('Failed to bulk update status:', err);
    } finally {
      setSaving(false);
    }
  };

  // -- CONFIG FIELD UPDATE (used by PipelineView smart fix modal) --

  const handleUpdateConfigField = async (field: keyof BrandConfig, value: string) => {
    if (!activeBrand) return;
    const updatedConfig = { ...activeBrand.config, [field]: value };
    const updatedBrand: DocBrand = {
      ...activeBrand,
      config: updatedConfig,
      updatedAt: new Date().toISOString(),
    };
    try {
      await saveBrand(updatedBrand);
      setActiveBrand(updatedBrand);
      setBrands(prev => prev.map(b => b.id === updatedBrand.id ? updatedBrand : b));
      console.log(`[Config] Updated ${field} = "${value}"`);
    } catch (err) {
      console.error(`Failed to update config field ${field}:`, err);
    }
  };

  // Brand save callback (used by ConfigView)
  const handleBrandSaved = (updatedBrand: DocBrand) => {
    setActiveBrand(updatedBrand);
    setBrands(prev => prev.map(b => b.id === updatedBrand.id ? updatedBrand : b));
  };

  // -- LOADING STATE --
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
        <div style={{
          width: 32, height: 32, border: `3px solid ${C.borderLight}`,
          borderTopColor: C.accent, borderRadius: '50%',
          animation: 'dc-spin 0.8s linear infinite',
          margin: '0 auto 12px',
        }} />
        <style>{`@keyframes dc-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize: 14 }}>Loading Document Operations...</div>
      </div>
    );
  }

  // -- MAIN RETURN --
  return (
    <div className="dc-wrap">
      {view === 'pipeline' && (
        <PipelineView
          documents={filteredDocuments}
          templates={filteredTemplates}
          brands={brands}
          activeBrand={activeBrand}
          libraryScore={libraryScore}
          pipelineGroups={pipelineGroups}
          nextRoundInfo={nextRoundInfo}
          totalDocs={totalDocs}
          generatedDocs={generatedDocs}
          blockerDocs={blockerDocs}
          attorneyDocs={attorneyDocs}
          activeTrack={activeTrack}
          setActiveTrack={setActiveTrack}
          trackCounts={trackCounts}
          generating={generating}
          generatingAll={generatingAll}
          genProgress={genProgress}
          auditProgress={auditProgress}
          genError={genError}
          genMsgIdx={genMsgIdx}
          searchQuery={searchQuery}
          activeFilters={activeFilters}
          collapsedStages={collapsedStages}
          showConflicts={showConflicts}
          saving={saving}
          setActiveBrand={setActiveBrand}
          openConfig={openConfig}
          openDocument={openDocument}
          setSearchQuery={setSearchQuery}
          toggleFilter={toggleFilter}
          toggleStage={toggleStage}
          setShowConflicts={setShowConflicts}
          generateDocument={(docId) => { generateSingle(docId); }}
          generateNextRound={(ri) => { collapseForGeneration(); generateNextRound(ri); }}
          generateDocuments={(phase) => { collapseForGeneration(); generateByPhase(phase); }}
          applyConfigToAll={applyConfigToAll}
          rescoreUngated={rescoreUngated}
          rescoreAll={rescoreAll}
          fixConflict={fixConflict}
          fixAllConflicts={fixAllConflicts}
          fixingConflicts={fixingConflicts}
          fixProgress={fixProgress}
          activeJobId={activeJobId}
          cancelJob={cancelJob}
          jobDocIds={jobDocIds}
          preRegenScores={preRegenScores}
          onUpdateConfigField={handleUpdateConfigField}
          bulkUpdateStatus={bulkUpdateStatus}
          clearGenError={clearGenError}
          downloadAllDocs={downloadAllDocs}
          exporting={exporting}
          regenerateBatch={(docs: DocDocument[]) => { generateBatch(docs, 'regen'); }}
        />
      )}
      {view === 'document' && (
        <DocumentView
          documents={documents}
          templates={templates}
          selectedDocId={selectedDocId}
          activeBrandId={activeBrand?.id ?? null}
          activeBrandConfig={activeBrand?.config ?? null}
          saving={saving}
          generating={generating}
          genError={genError}
          genResult={genResult}
          genMsgIdx={genMsgIdx}
          auditProgress={auditProgress}
          goBack={goBack}
          generateDocument={(docId) => generateSingle(docId)}
          updateDocStatus={updateDocStatus}
          onDocumentUpdated={handleDocUpdated}
          clearGenError={clearGenError}
          clearGenResult={clearGenResult}
        />
      )}
      {view === 'config' && (
        <ConfigView
          activeBrand={activeBrand}
          goBack={goBack}
          onBrandSaved={handleBrandSaved}
        />
      )}
    </div>
  );
}
